import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { BAN_DO_DICH_VU, DANH_MUC_DICH_VU } from '../../../lib/dich-vu-cong';
import type { ApiError } from '../../../lib/errors';
import { nguoiDungRepository } from '../../../lib/repositories/nguoi-dung.repository';
import { useAuthStore } from '../../../stores/auth';
import { tinhQuyenCongTenant } from '../nghiep-vu/quyen-cong-tenant';
import type { MyContext } from '../nghiep-vu/types';

export const KHOA_NGU_CANH = (userId: string) => ['users', 'me', 'context', userId] as const;

/**
 * Tổ chức vừa đổi trạng thái hay chính sách (`tenant.changed` của kênh sự
 * kiện): tải lại ngữ cảnh ngay, để băng tạm ngưng, màn chặn và quyền theo gói
 * cập nhật mà không chờ hết `staleTime`.
 */
export function lamMoiNguCanhCuaToi(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['users', 'me', 'context'] });
}

/** 4xx là câu trả lời của server (khoá, hết phiên): thử lại chỉ làm người dùng chờ thêm. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/**
 * Ghi quyền tính từ ngữ cảnh vào hồ sơ đang đăng nhập.
 *
 * Gọi ngay trong `queryFn` thay vì đồng bộ bằng `useEffect`: mọi lần tải lại
 * (đổi tab quay về, hết `staleTime`) đều đi qua đây, nên gói vừa đổi là thanh
 * điều hướng đổi theo mà không có nhịp nào hồ sơ lệch với dữ liệu vừa nhận.
 */
function apDung(userId: string, ctx: MyContext): void {
  const q = tinhQuyenCongTenant(ctx, DANH_MUC_DICH_VU, BAN_DO_DICH_VU);
  useAuthStore.setState((s) =>
    s.user && s.user.id === userId
      ? {
          user: {
            ...s.user,
            roles: [q.khoaVaiTro],
            permissions: q.permissions,
            dichVu: q.dichVu,
            quyenModule: ctx.permissions,
            trangThaiTenant: ctx.tenant_status,
          },
        }
      : s,
  );
}

/**
 * Ngữ cảnh quyền của người đang đăng nhập ở cổng tenant (ADR-0034).
 *
 * `batBuoc = false` thì không gọi: tài khoản mẫu và nhân sự nền tảng không có
 * ngữ cảnh tenant (xem `NguCanhGate`).
 */
export function useNguCanhCuaToi(batBuoc: boolean) {
  const userId = useAuthStore((s) => s.user?.id);
  const vaiTroLop1 = useAuthStore((s) => s.user?.vaiTroLop1);

  return useQuery<MyContext, ApiError>({
    queryKey: KHOA_NGU_CANH(userId ?? ''),
    queryFn: async () => {
      const ctx = await nguoiDungRepository.nguCanhCuaToi(vaiTroLop1 ?? []);
      if (userId) apDung(userId, ctx);
      return ctx;
    },
    enabled: batBuoc && Boolean(userId),
    staleTime: 60_000,
    retry: thuLai,
  });
}
